/* eslint-disable import/no-extraneous-dependencies */

import { useForm } from "react-hook-form";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormItem,
  FormControl,
  Input,
} from "@repo/ui";
import { useNavigate } from "react-router-dom";
import { ExcelRenderer } from "react-excel-renderer";
import { useEffect, useState, useCallback } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import moment from "moment";
import sweetAlert from "components/sweet-alert";
import { Label } from "ui/label";
import { PATHS } from "routes/paths";
import { useCloseBulkCashTicket } from "services/tickets";
import Loader from "ui/page-loader";
import { DATE_TIME_FORMAT } from "utils/constant";
import { excelTimestampToJSDate } from "pages/tickets/utils";

export default function CloseBulkCashTickets() {
  const [closingTickets, setClosingTickets] = useState([]);
  const navigate = useNavigate();
  const closingForm = useForm({ defaultValues: { file: null } });

  const {
    mutate: closeTicket,
    data: closeTicketRes,
    isPending: closingTicket,
  } = useCloseBulkCashTicket();

  useEffect(() => {
    if (closeTicketRes) {
      navigate(PATHS.openTickets);
    }
  }, [closeTicketRes, navigate]);

  const getTime = (excelTimestamp) => {
    return excelTimestampToJSDate(excelTimestamp);
  };

  const processExcelData = useCallback((err, resp, field) => {
    if (err) {
      sweetAlert({ title: "Operation Failure", text: err, icon: "error" });
      return;
    }

    const tickets = resp.rows
      .slice(1) // Skip header row
      .filter((row) => row?.length)
      .map((row) => {
        const keys = resp.rows[0].map((key) => key.split(" ")[0]);
        const ticket = {};

        row.forEach((item, index) => {
          const key = keys[index];
          const isTimeField =
            key === "AttendedStartTime" || key === "AttendedEndTime";

          ticket[key] = isTimeField && item ? getTime(item) : (item ?? "");
        });

        return ticket;
      });

    setClosingTickets(tickets);
    field.onChange(tickets);
  }, []);

  const closingFileHandler = useCallback(
    (event, field) => {
      if (!event?.target?.files?.[0]) {
        sweetAlert({
          title: "File Upload Failed",
          text: "Cannot Upload File",
          icon: "error",
        });
        return;
      }

      ExcelRenderer(event.target.files[0], (err, resp) =>
        processExcelData(err, resp, field)
      );
    },
    [processExcelData]
  );

  const validateTicketData = useCallback((ticket) => {
    return (
      ticket.CaseNo &&
      moment(ticket.AttendedStartTime, DATE_TIME_FORMAT).isValid() &&
      moment(ticket.AttendedEndTime, DATE_TIME_FORMAT).isValid()
    );
  }, []);

  const confirmCloseTickets = useCallback(
    (payload) => {
      sweetAlert({
        title: "Operation Confirmation",
        text: "Are you sure you want to close tickets",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        onConfirm: () => closeTicket(payload),
      });
    },
    [closeTicket]
  );

  const onSaveClosingBulk = useCallback(() => {
    const invalidTicket = closingTickets.find(
      (ticket) => !validateTicketData(ticket)
    );

    if (invalidTicket) {
      sweetAlert({
        title: "Invalid Data",
        text: "Required Fields Are Missing In List",
        icon: "error",
      });
      return;
    }

    const payload = closingTickets.map((ticket) => ({
      caseNo: ticket.CaseNo,
      attendedStartTime: moment
        .utc(ticket.AttendedStartTime, "DD/MM/YYYY HH:mm A")
        .toISOString(),
      attendedEndTime: moment
        .utc(ticket.AttendedEndTime, "DD/MM/YYYY HH:mm A")
        .toISOString(),
      resolutionCode: ticket.ResolutionCode || "",
      resolutionNotes: ticket.ResolutionNotes || "",
    }));

    confirmCloseTickets(payload);
  }, [closingTickets, validateTicketData, confirmCloseTickets]);

  return (
    <>
      {closingTicket ? <Loader /> : null}

      <Card className="mt-4 flex flex-col flex-1 overflow-hidden">
        <CardHeader>
          <CardTitle>
            <div className="flex flex-row justify-between w-full gap-2">
              Closed Assigned Tickets
              <Button
                variant="secondary"
                onClick={closingForm.handleSubmit(onSaveClosingBulk)}
              >
                Save
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <Form {...closingForm}>
          <form className="space-y-8 custom-form-v2">
            <CardContent className="w-full flex flex-row items-center justify-between flex-wrap gap-4">
              <div className="form-row-d-50 [&>.space-y-2]:!w-full">
                <FormField
                  control={closingForm.control}
                  name="file"
                  rules={{ required: "Please Upload File" }}
                  render={({ field }) => (
                    <FormItem>
                      <Label label="Upload File" isRequired />
                      <FormControl>
                        <div>
                          <Input
                            style={
                              closingForm.formState?.errors?.file
                                ? { borderColor: "red" }
                                : {}
                            }
                            type="file"
                            onChange={(event) =>
                              closingFileHandler(event, field)
                            }
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          />
                          {closingForm.formState?.errors?.file ? (
                            <p className="text-destructive text-xs">
                              Please Upload File
                            </p>
                          ) : null}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {closingTickets.length > 0 && (
                <Grid data={closingTickets} className="list-tables-v1">
                  <GridColumn title="Ticket Number" field="CaseNo" />
                  <GridColumn
                    title="Attended Start Time"
                    field="AttendedStartTime"
                  />
                  <GridColumn
                    title="Attended End Time"
                    field="AttendedEndTime"
                  />
                  <GridColumn title="Resolution Code" field="ResolutionCode" />
                  <GridColumn
                    title="Resolution Notes"
                    field="ResolutionNotes"
                  />
                </Grid>
              )}
            </CardContent>
          </form>
        </Form>
      </Card>
    </>
  );
}
